import { router } from "@inertiajs/react";
import { Clock, XCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";

export type StatusScreenProps = {
  state: "pending" | "rejected";
  org_name: string;
  request: {
    org_node_path: string;
    role_note: string;
    submitted_at: string;
    review_note: string | null;
  };
};

// Shown to a self-signup who submitted an access request but has no roles yet:
// either awaiting an admin's review, or rejected. No app access from here — the
// only action is to sign out.
export default function StatusScreen({ state, org_name, request }: StatusScreenProps) {
  const pending = state === "pending";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 px-4">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-[30px] h-[30px] rounded-md bg-foreground text-background flex items-center justify-center text-sm font-semibold">A</div>
        <span className="text-sm font-semibold text-foreground" style={{ letterSpacing: "-0.01em" }}>ATLAS AI</span>
      </div>

      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className={`mb-2 flex items-center gap-2 ${pending ? "text-warning" : "text-destructive"}`}>
            {pending ? <Clock size={18} /> : <XCircle size={18} />}
            <CardTitle className="text-xl">
              {pending ? "Awaiting approval" : "Request declined"}
            </CardTitle>
          </div>
          <CardDescription>
            {pending
              ? `Your request to join ${org_name} is with an admin. You'll get a Slack message once it's approved.`
              : `An admin declined your request to join ${org_name}. Contact your admin if you think this is a mistake.`}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground">Requested organization</dt>
              <dd className="text-foreground">{request.org_node_path}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">You described your role as</dt>
              <dd className="text-foreground">“{request.role_note}”</dd>
            </div>
            {!pending && request.review_note && (
              <div>
                <dt className="text-muted-foreground">Reason</dt>
                <dd className="text-foreground">{request.review_note}</dd>
              </div>
            )}
          </dl>

          <Button variant="outline" className="w-full" onClick={() => router.delete("/logout")}>
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
